import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import cpApi from "@/lib/clientPortalApi";

const CpAuthContext = createContext(null);

export function ClientPortalAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await cpApi.get("/client-portal/auth/me");
      setUser(r.data.user);
      setClient(r.data.client);
      setCompanies(r.data.companies || []);
    } catch (e) {
      setUser(null);
      setClient(null);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const r = await cpApi.post("/client-portal/auth/login", { email, password });
    if (r.data?.token) {
      localStorage.setItem("cp_token", r.data.token);
    }
    setUser(r.data.user);
    setCompanies(r.data.companies || []);
    await checkAuth();
    return r.data;
  }, [checkAuth]);

  const switchCompany = useCallback(async (clientId) => {
    try {
      const r = await cpApi.post("/client-portal/auth/switch-company", { client_id: clientId });
      if (r.data?.token) localStorage.setItem("cp_token", r.data.token);
      await checkAuth();
      toast.success("Switched company");
      window.location.href = "/client-portal/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch");
    }
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await cpApi.post("/client-portal/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("cp_token");
    setUser(null); setClient(null); setCompanies([]);
    window.location.href = "/client-portal/login";
  }, []);

  const value = useMemo(
    () => ({ user, client, companies, loading, checkAuth, login, logout, switchCompany }),
    [user, client, companies, loading, checkAuth, login, logout, switchCompany],
  );
  return <CpAuthContext.Provider value={value}>{children}</CpAuthContext.Provider>;
}

export const useCpAuth = () => useContext(CpAuthContext);
